import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, ChevronLeft, Upload, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "", qualification: "", clinic_name: "", working_at: [], specialties: [], serving_from: "", address: "",
  languages: [], about: "", register_no: "", mobile: "", state_medical_council: "",
};

export default function Profile() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoBust, setLogoBust] = useState(0);
  const [newPlace, setNewPlace] = useState("");
  const [newSpec, setNewSpec] = useState("");
  const [newLang, setNewLang] = useState("");
  const logoInputRef = React.useRef(null);

  useEffect(() => {
    if (!user) return;
    setD({
      ...empty, ...user,
      working_at: user.working_at || [], specialties: user.specialties || [], languages: user.languages || [],
    });
  }, [user]);

  const addChip = (field, value, setter) => {
    if (!value.trim()) return;
    setD({ ...d, [field]: [...(d[field] || []), value.trim()] });
    setter("");
  };

  const removeChip = (field, i) => setD({ ...d, [field]: d[field].filter((_, ix) => ix !== i) });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/profile", d);
      setUser(data);
      toast.success("Profile updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser({ ...(user || {}), logo_path: data.logo_path });
      setD((s) => ({ ...s, logo_path: data.logo_path }));
      setLogoBust(Date.now());
      toast.success("Logo updated — visible on your prescriptions");
    } catch (e) { toast.error(e?.response?.data?.detail || "Logo upload failed"); }
    finally { setLogoBusy(false); }
  };

  const completeness = () => {
    const fields = ["name", "qualification", "working_at", "specialties", "serving_from", "address", "languages", "register_no", "mobile", "state_medical_council"];
    let filled = 0;
    fields.forEach((f) => { const v = d[f]; if (Array.isArray(v) ? v.length : (v || "").toString().trim()) filled++; });
    return Math.round((filled / fields.length) * 100);
  };
  const pct = completeness();

  return (
    <Layout>
      <div className="card-soft overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-[#EFECE5]">
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-[#47554D] hover:text-[#0A4C36]"><ChevronLeft size={16} /> Back</button>
          <div className="text-sm text-[#0A4C36] font-semibold">{pct}% completed</div>
          <button onClick={save} disabled={busy} className="btn-emerald" data-testid="profile-update-btn">{busy ? "Saving…" : "Update"}</button>
        </div>
        <div className="h-1 w-full bg-[#EFECE5]">
          <div className="h-1 bg-[#0A4C36] transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="grid md:grid-cols-3 gap-6 p-8">
          <aside className="md:col-span-1 bg-[#E6F0EC] rounded-3xl p-6">
            <div className="relative w-32 h-32 mx-auto">
              {d.logo_path ? (
                <img
                  key={logoBust}
                  src={`${API}/public/logo/${user?.id}?v=${logoBust}`}
                  alt="Clinic logo"
                  className="w-32 h-32 rounded-full object-cover bg-white border border-white"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  data-testid="profile-logo-preview"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-white flex items-center justify-center text-[#0A4C36] text-4xl font-semibold" style={{ fontFamily: "Instrument Sans" }}>
                  {d.name?.[0]?.toUpperCase() || "D"}
                </div>
              )}
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden"
                data-testid="profile-logo-input"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }}
              />
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                disabled={logoBusy}
                data-testid="profile-logo-upload-btn"
                className="absolute -bottom-1 right-0 w-9 h-9 rounded-full bg-[#0A4C36] text-white shadow-md hover:bg-[#0e5c42] flex items-center justify-center disabled:opacity-50"
                title="Upload clinic logo"
              >
                <Upload size={14} />
              </button>
            </div>
            <div className="text-center text-[11px] text-[#5C7568] mt-3">
              {logoBusy ? "Uploading…" : d.logo_path ? "Logo appears on prescriptions" : "Add a clinic logo (PNG/JPG, ≤ 2 MB)"}
            </div>
            <div className="mt-6 space-y-4 text-sm">
              <Info label="Mail ID" value={user?.email} />
              <Info label="Mobile No" value={d.mobile} />
              <Info label="Register No" value={d.register_no} />
              <Info label="State Medical Council" value={d.state_medical_council} />
            </div>
          </aside>

          <section className="md:col-span-2 space-y-5">
            <Field label="Name *"><input className="input-clean" data-testid="prof-name" value={d.name || ""} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="Dr. Makesh Gopalakrishnan" /></Field>
            <Field label="Qualification *"><input className="input-clean" data-testid="prof-qualification" value={d.qualification || ""} onChange={(e) => setD({ ...d, qualification: e.target.value })} placeholder="M.B.B.S, M.D, PhD" /></Field>
            <Field label="Clinic / Practice name"><input className="input-clean" data-testid="prof-clinic" value={d.clinic_name || ""} onChange={(e) => setD({ ...d, clinic_name: e.target.value })} placeholder="Apollo Clinic — Neelankarai" /></Field>

            <ChipInput label="Working at *" values={d.working_at || []} onAdd={() => addChip("working_at", newPlace, setNewPlace)} onRemove={(i) => removeChip("working_at", i)}
              value={newPlace} setValue={setNewPlace} placeholder="Eg. Apollo Clinic" testId="prof-place" />

            <ChipInput label="Specialty *" values={d.specialties || []} onAdd={() => addChip("specialties", newSpec, setNewSpec)} onRemove={(i) => removeChip("specialties", i)}
              value={newSpec} setValue={setNewSpec} placeholder="Gynecologist, Cardiology…" testId="prof-spec" />

            <div className="grid grid-cols-2 gap-4">
              <Field label="Serving from"><input type="date" className="input-clean" value={d.serving_from || ""} onChange={(e) => setD({ ...d, serving_from: e.target.value })} /></Field>
              <Field label="Mobile no"><input className="input-clean" value={d.mobile || ""} onChange={(e) => setD({ ...d, mobile: e.target.value })} placeholder="+91 …" /></Field>
            </div>

            <Field label="Address"><textarea className="input-clean min-h-[80px] py-3" value={d.address || ""} onChange={(e) => setD({ ...d, address: e.target.value })} placeholder="Neelankarai, Chennai" /></Field>

            <ChipInput label="Languages known *" values={d.languages || []} onAdd={() => addChip("languages", newLang, setNewLang)} onRemove={(i) => removeChip("languages", i)}
              value={newLang} setValue={setNewLang} placeholder="English, Tamil, Telugu…" testId="prof-lang" />

            <div className="grid grid-cols-2 gap-4">
              <Field label="Register No"><input className="input-clean" value={d.register_no || ""} onChange={(e) => setD({ ...d, register_no: e.target.value })} placeholder="AB16263663" /></Field>
              <Field label="State Medical Council"><input className="input-clean" value={d.state_medical_council || ""} onChange={(e) => setD({ ...d, state_medical_council: e.target.value })} placeholder="Tamil Nadu (0012)" /></Field>
            </div>

            <Field label="About"><textarea className="input-clean min-h-[80px] py-3" value={d.about || ""} onChange={(e) => setD({ ...d, about: e.target.value })} placeholder="Passionate doctor…" /></Field>
          </section>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, children }) {
  return (<div><label className="field-label block mb-2">{label}</label>{children}</div>);
}
function Info({ label, value }) {
  return (<div><div className="text-[10px] uppercase tracking-widest text-[#5C7568]">{label}</div><div className="text-sm text-[#1F2A24] mt-0.5">{value || "—"}</div></div>);
}
function ChipInput({ label, values, value, setValue, onAdd, onRemove, placeholder, testId }) {
  return (
    <Field label={label}>
      <div className="flex gap-2">
        <input className="input-clean flex-1" value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} data-testid={testId} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }} />
        <button onClick={onAdd} className="w-11 h-11 rounded-full bg-[#0A4C36] text-white flex items-center justify-center" data-testid={`${testId}-add`}><Plus size={16} /></button>
      </div>
      {values.length > 0 && (
        <div className="mt-3">
          <div className="text-xs text-[#8A9992] mb-2">Added ({String(values.length).padStart(2, "0")})</div>
          <div className="flex flex-wrap gap-2">
            {values.map((v, i) => (
              <span key={i} className="chip bg-white border border-[#EFECE5]">{v}
                <button onClick={() => onRemove(i)} className="ml-1 text-[#8A9992] hover:text-red-500"><X size={12} /></button>
              </span>
            ))}
          </div>
        </div>
      )}
    </Field>
  );
}
